var __importDefault = (this && this.__importDefault) || function (mod) {
  return (mod && mod.__esModule) ? mod : { "default": mod };
};
/**
 * DayPicker Component
 * Renders a monthly calendar grid with day cells and events
 */
import React, { useMemo, useCallback, memo } from 'react';
import { CalendarCell } from '../CalendarCell';
import { EventBar } from '../EventBar';
import { getRequiredCellHeight } from '../../utils/eventLayout';
import { styles } from './DayPicker.styles';
/**
 * Compare two dates on calendar day only.
 *
 * Grid dates are produced at midnight (`startOfMonth`/`addDays`) whereas the
 * roving focus date carries a wall-clock time, so an exact `getTime()` compare
 * never matches and would leave every cell with `tabIndex={-1}` - making the
 * grid unreachable by keyboard. Mirrors YearView's comparison.
 */
const isSameCalendarDay = (a, b) => a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
/**
 * DayPicker - Monthly calendar grid component
 */
export const DayPicker = memo(({ rows, dayHeaders, eventMap, config, focusedDate, title, maxVisibleEvents = 2, onDayClick, onEventClick, onMoreClick, onAddEventClick, onDayFocus, getDayCustomClass, getDayCustomAriaLabel, idPrefix = 'daypicker', onTitleClick, titleActionLabel, compact = false, }) => {
    // Get events for a specific day
    const getEventsForDay = useCallback((day) => {
        return eventMap[day.key] || [];
    }, [eventMap]);
    // Handle day click
    const handleDayClick = useCallback((day, e) => {
        const events = getEventsForDay(day);
        onDayClick === null || onDayClick === void 0 ? void 0 : onDayClick(day, events, e);
    }, [getEventsForDay, onDayClick]);
    // Handle event click
    const handleEventClick = useCallback((event, day, e) => {
        e.stopPropagation();
        onEventClick === null || onEventClick === void 0 ? void 0 : onEventClick(event, day, e);
    }, [onEventClick]);
    // Handle more click
    const handleMoreClick = useCallback((day, e) => {
        e.stopPropagation();
        const events = getEventsForDay(day);
        onMoreClick === null || onMoreClick === void 0 ? void 0 : onMoreClick(day, events, e);
    }, [getEventsForDay, onMoreClick]);
    // Render day headers
    const renderDayHeaders = useMemo(() => (React.createElement("tr", { style: styles.headerRow, role: "row" },
        config.showWeeks && React.createElement("th", { style: styles.weekHeader, scope: "col" }, "#"),
        dayHeaders.map((header, index) => (React.createElement("th", { key: `header-${index}`, style: compact ? Object.assign(Object.assign({}, styles.dayHeader), styles.dayHeaderCompact) : styles.dayHeader, scope: "col", "aria-label": header }, compact ? header.charAt(0) : header))))), [dayHeaders, config.showWeeks, compact]);
    // Render events for a day
    const renderDayEvents = useCallback((day, events) => {
        // Mini-months only signal presence of events; the main grid renders named bars.
        if (compact) {
            if (events.length === 0) {
                return null;
            }
            return (React.createElement("div", { style: styles.compactDotRow, "aria-hidden": "true" }, events.slice(0, 3).map((event) => (React.createElement("span", { key: `dot-${event.id}-${day.key}`, style: Object.assign(Object.assign({}, styles.compactDot), { backgroundColor: event.bgcolor || '#0078d4' }), title: event.name })))));
        }
        const visibleEvents = events.filter(e => { var _a; return ((_a = e.order) !== null && _a !== void 0 ? _a : 0) <= maxVisibleEvents; });
        const hiddenCount = events.length - visibleEvents.length;
        return (React.createElement("div", { style: styles.eventsContainer },
            visibleEvents.map((event) => (event.startPaint && (React.createElement(EventBar, { key: `event-${event.id}-${day.key}`, event: event, spanDays: event.paintBoxLength || 1, order: event.order, isStart: event.startPaint, showName: true, compact: false, onEventClick: (evt, e) => handleEventClick(evt, day, e) })))),
            hiddenCount > 0 && (React.createElement("button", { style: styles.moreButton, onClick: (e) => handleMoreClick(day, e), "aria-label": `${hiddenCount} more events. Click to view all.` },
                "+",
                hiddenCount,
                " more"))));
    }, [maxVisibleEvents, handleEventClick, handleMoreClick, compact]);
    // Render a single row
    const renderRow = useCallback((row, rowIndex) => {
        /*
         * Size the week to its deepest day. Event bars are absolutely positioned and stack downward
         * from a fixed offset, so a day holding more bars than the cell is tall paints straight over
         * the following week - which is what happened with a fixed 80px cell in `expandedMode`, where
         * there is no "+N more" cap and every event gets a bar. The row cannot clip instead: multi-day
         * bars intentionally overflow horizontally, and CSS cannot clip one axis alone.
         */
        const rowStackDepth = compact
            ? 0
            : row.days.reduce((deepest, day) => {
                const orders = getEventsForDay(day)
                    .filter((e) => { var _a; return ((_a = e.order) !== null && _a !== void 0 ? _a : 0) <= maxVisibleEvents; })
                    .map((e) => { var _a; return (_a = e.order) !== null && _a !== void 0 ? _a : 0; });
                return orders.length ? Math.max(deepest, ...orders) : deepest;
            }, 0);
        const rowStyle = compact
            ? styles.row
            : Object.assign(Object.assign({}, styles.row), { height: `${getRequiredCellHeight(rowStackDepth)}px` });
        return (React.createElement("tr", { key: `row-${rowIndex}`, style: rowStyle, role: "row" },
            config.showWeeks && row.weekNumber !== undefined && (React.createElement("td", { style: styles.weekCell, role: "rowheader", "aria-label": `Week ${row.weekNumber}` }, row.weekNumber)),
            row.days.map((day, colIndex) => {
                const events = getEventsForDay(day);
                const isFocused = focusedDate ? isSameCalendarDay(day.date, focusedDate) : false;
                const customClass = getDayCustomClass === null || getDayCustomClass === void 0 ? void 0 : getDayCustomClass(day.date);
                const customAriaLabel = getDayCustomAriaLabel === null || getDayCustomAriaLabel === void 0 ? void 0 : getDayCustomAriaLabel(day.date);
                return (React.createElement(CalendarCell, { key: day.key, day: day, events: events, isFocused: isFocused, maxVisibleEvents: maxVisibleEvents, showAddEventOnHover: !compact && !!onAddEventClick, showMoreIndicator: !compact, compact: compact, onDayClick: (d, evts, e) => handleDayClick(d, e), onAddEventClick: onAddEventClick, onFocus: () => onDayFocus === null || onDayFocus === void 0 ? void 0 : onDayFocus(day), customClass: customClass, customAriaLabel: customAriaLabel, rowIndex: rowIndex, colIndex: colIndex, renderEvents: (_evts, d) => renderDayEvents(d, getEventsForDay(d)) }));
            })));
    }, [
        config.showWeeks,
        getEventsForDay,
        focusedDate,
        getDayCustomClass,
        getDayCustomAriaLabel,
        maxVisibleEvents,
        handleDayClick,
        onAddEventClick,
        onDayFocus,
        idPrefix,
        renderDayEvents,
        compact,
    ]);
    return (React.createElement("div", { style: compact ? Object.assign(Object.assign({}, styles.container), styles.containerCompact) : styles.container },
        title && (onTitleClick ? (React.createElement("button", { type: "button", style: compact
                ? Object.assign(Object.assign(Object.assign({}, styles.title), styles.titleCompact), styles.titleButton) : Object.assign(Object.assign({}, styles.title), styles.titleButton), onClick: onTitleClick, "aria-label": titleActionLabel || title }, title)) : (React.createElement("div", { style: compact ? Object.assign(Object.assign({}, styles.title), styles.titleCompact) : styles.title, "aria-live": "polite" }, title))),
        React.createElement("table", { style: compact ? Object.assign(Object.assign({}, styles.table), styles.tableCompact) : styles.table, role: "grid", "aria-label": title || 'Calendar' },
            React.createElement("thead", null, renderDayHeaders),
            React.createElement("tbody", null, rows.map((row, index) => renderRow(row, index))))));
});
DayPicker.displayName = 'DayPicker';
export default DayPicker;
//# sourceMappingURL=DayPicker.js.map